import { useState } from "react";
import { toast } from "sonner";
import { submitQuote } from "../lib/api";
import { INSURANCE_TYPES } from "../lib/constants";
import { ArrowRight, Loader2 } from "lucide-react";

const initial = {
  full_name: "",
  email: "",
  phone: "",
  insurance_type: INSURANCE_TYPES[0],
  zip_code: "",
  message: "",
};

export const QuoteForm = ({ compact = false }) => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const onChange = (e) =>
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email, and phone.");
      return;
    }
    setLoading(true);
    try {
      await submitQuote(form);
      toast.success("Quote request received. We'll be in touch within one business day.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please call us at 229-733-3500.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      data-testid="quote-form"
      className={`bg-white border border-gray-200 ${compact ? "p-6" : "p-8 lg:p-10"} rounded-sm`}
    >
      <div className="flex items-center gap-3 mb-6">
        <span className="eyebrow">Free & No Obligation</span>
      </div>
      <h3 className={`font-serif text-[color:var(--isa-navy)] ${compact ? "text-2xl" : "text-3xl"} leading-tight`}>
        Request a Quote
      </h3>
      <p className="mt-2 text-sm font-sans text-gray-600">
        Tell us a little about what you're looking to protect — we'll match you with
        the right coverage from top-rated carriers.
      </p>
      <div className="gold-divider" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-2">
          <label className="isa-label" htmlFor="quote-full-name">Full Name *</label>
          <input
            id="quote-full-name"
            name="full_name"
            type="text"
            className="isa-input"
            value={form.full_name}
            onChange={onChange}
            required
            data-testid="quote-input-name"
            placeholder="Jane Doe"
          />
        </div>
        <div>
          <label className="isa-label" htmlFor="quote-email">Email *</label>
          <input
            id="quote-email"
            name="email"
            type="email"
            className="isa-input"
            value={form.email}
            onChange={onChange}
            required
            data-testid="quote-input-email"
            placeholder="you@email.com"
          />
        </div>
        <div>
          <label className="isa-label" htmlFor="quote-phone">Phone *</label>
          <input
            id="quote-phone"
            name="phone"
            type="tel"
            className="isa-input"
            value={form.phone}
            onChange={onChange}
            required
            data-testid="quote-input-phone"
            placeholder="(229) 555-0123"
          />
        </div>
        <div>
          <label className="isa-label" htmlFor="quote-type">Insurance Type *</label>
          <select
            id="quote-type"
            name="insurance_type"
            className="isa-input bg-white"
            value={form.insurance_type}
            onChange={onChange}
            required
            data-testid="quote-input-type"
          >
            {INSURANCE_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="isa-label" htmlFor="quote-zip">ZIP Code</label>
          <input
            id="quote-zip"
            name="zip_code"
            type="text"
            className="isa-input"
            value={form.zip_code}
            onChange={onChange}
            data-testid="quote-input-zip"
            placeholder="31601"
            maxLength={10}
          />
        </div>
        <div className="md:col-span-2">
          <label className="isa-label" htmlFor="quote-message">Tell Us More (optional)</label>
          <textarea
            id="quote-message"
            name="message"
            rows={4}
            className="isa-input"
            value={form.message}
            onChange={onChange}
            data-testid="quote-input-message"
            placeholder="What would you like covered, current carriers, or any specific questions..."
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="btn-primary mt-7 w-full md:w-auto"
        data-testid="quote-submit-button"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" strokeWidth={1.8} />}
        {loading ? "Submitting..." : "Get My Quote"}
      </button>
      <p className="mt-4 text-xs font-sans text-gray-500">
        We never share your information. Quotes are always complimentary.
      </p>
    </form>
  );
};

export default QuoteForm;
